import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { APIRoutes } from '@/services/api.routes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faSpinner, faCheckCircle, faExclamationTriangle } from '@/fontawesome';
import type { UserID } from '@/models';

type OptOutType = 'courseMailSequence' | 'videoPublished' | 'recommendationMails';

const EMAIL_TYPE_NAMES: Record<OptOutType, string> = {
  courseMailSequence: 'Kurs E-Mail-Sequenz',
  videoPublished: 'Video-Veröffentlichungsbenachrichtigungen',
  recommendationMails: 'Empfehlungs-E-Mails'
};

const EMAIL_TYPE_DESCRIPTIONS: Record<OptOutType, string> = {
  courseMailSequence: 'Sie erhalten keine E-Mails mehr zu Kursfortschritten und automatischen Kurs-Sequenzen.',
  videoPublished: 'Sie erhalten keine E-Mails mehr bei neuen Video-Veröffentlichungen.',
  recommendationMails: 'Sie erhalten keine E-Mails mehr bei neuen Handelideen.'
};

export default function EmailOptOutPage() {
  const { userid, type, key } = useParams<{ userid: string; type: string; key: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate type parameter
  const isValidType = type === 'courseMailSequence' || type === 'videoPublished' || type === 'recommendationMails';
  const emailType = isValidType ? (type as OptOutType) : null;

  const handleOptOut = async () => {
    if (userid === undefined || emailType === null || key === undefined) {
      setError('Ungültiger Link: Parameter fehlen');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await APIRoutes.emailOptOut(userid as UserID, emailType, key);

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.message || 'E-Mail-Abmeldung fehlgeschlagen');
      }
    } catch (err) {
      console.error('Failed to opt out:', err);
      setError('E-Mail-Abmeldung fehlgeschlagen. Der Link ist möglicherweise ungültig oder abgelaufen.');
    } finally {
      setLoading(false);
    }
  };

  // Invalid parameters
  if (userid === undefined || emailType === null || key === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-16 h-16 text-red-500 mb-4 mx-auto" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Ungültiger Link
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Der Link ist ungültig oder fehlerhaft. Bitte überprüfen Sie den Link aus Ihrer E-Mail.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full text-center">
          <FontAwesomeIcon icon={faCheckCircle} className="w-16 h-16 text-green-500 mb-4 mx-auto" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Erfolgreich abgemeldet
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Sie wurden erfolgreich von <strong>{EMAIL_TYPE_NAMES[emailType]}</strong> abgemeldet.
          </p>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {EMAIL_TYPE_DESCRIPTIONS[emailType]}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            Sie können Ihre E-Mail-Einstellungen jederzeit in Ihren Account-Einstellungen ändern.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  // Error state
  if (error !== null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-16 h-16 text-red-500 mb-4 mx-auto" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Fehler
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setError(null);
                handleOptOut();
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
            >
              Erneut versuchen
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              Zur Startseite
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-16 h-16 text-blue-600 mb-4 mx-auto animate-spin" />
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Wird abgemeldet...
          </p>
        </div>
      </div>
    );
  }

  // Confirmation screen (initial state)
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-md w-full text-center">
        <FontAwesomeIcon icon={faEnvelope} className="w-16 h-16 text-blue-600 mb-4 mx-auto" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          E-Mail-Benachrichtigungen abmelden
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-2">
          Möchten Sie sich wirklich von <strong>{EMAIL_TYPE_NAMES[emailType]}</strong> abmelden?
        </p>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {EMAIL_TYPE_DESCRIPTIONS[emailType]}
        </p>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Sie können Ihre E-Mail-Einstellungen jederzeit in Ihren Account-Einstellungen wieder ändern.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleOptOut}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer transition-colors"
          >
            Ja, abmelden
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
